import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'reactstrap';
import {
    ShoppingCart, User, Calendar, Package, Building2,
    Mail, CreditCard, ChevronDown, ChevronUp, FileText, ShieldCheck, Truck, Receipt,
    AlertTriangle, Download, RotateCcw, CheckCircle2, Search, X, Pencil, LockOpen
} from 'lucide-react';
import { API_URL } from '../utils/Constants/URLS';
import { useSelector } from 'react-redux'
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Logo from '../../images/greenergy-logo.png';
import { useNavigate } from 'react-router-dom';
import EditPurchaseOrder from './EditPurchaseOrder';

const ViewPO = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [expandedInspectionRows, setExpandedInspectionRows] = useState({});
    const [inspectionData, setInspectionData] = useState({});
    const [loadingInspection, setLoadingInspection] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    // Debit Note Modal
    const [debitNoteModal, setDebitNoteModal] = useState({ isOpen: false, po: null });
    const [debitNoteForm, setDebitNoteForm] = useState({ invoiceNo: '', otherReferences: '', billingAddress: '', billingLocation: '', billingPin: '', shippingAddress: '', shippingLocation: '', shippingPin: '', lineItems: [] });
    const [debitNoteErrors, setDebitNoteErrors] = useState({});
    const [submittingDebitNote, setSubmittingDebitNote] = useState(false);

    // Edit PO Modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedEditPo, setSelectedEditPo] = useState(null);
    const [openingPoId, setOpeningPoId] = useState(null);

    // Tracks per-inspection sync state: { [inspectionId]: 'syncing' | 'done' | 'error' }
    const [syncingTallyInViewPo, setSyncingTallyInViewPo] = useState({});

    const token = localStorage.getItem('token');
    const userDetails = useSelector(store => store.user)

    // Debounced re-fetch whenever search query changes
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchPurchaseOrders(searchQuery);
        }, 1000);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const fetchPurchaseOrders = async (query = '') => {
        try {
            setLoading(true);
            const trimmed = query ? query.trim() : '';
            const url = trimmed
                ? `${API_URL}purchase-orders?search=${encodeURIComponent(trimmed)}`
                : `${API_URL}purchase-orders`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const result = await response.json();
            if (result.error) throw new Error(result.message);
            setData(result.data.docs);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleRow = (id) => {
        setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleInspectionStatus = async (poId) => {
        if (expandedInspectionRows[poId]) {
            setExpandedInspectionRows(prev => ({ ...prev, [poId]: false }));
            return;
        }

        try {
            setLoadingInspection(prev => ({ ...prev, [poId]: true }));
            const response = await fetch(`${API_URL}material-inspections/by-po/${poId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            const result = await response.json();
            if (result.error) throw new Error(result.message);

            setInspectionData(prev => ({ ...prev, [poId]: result.data }));
            setExpandedInspectionRows(prev => ({ ...prev, [poId]: true }));
        } catch (err) {
            console.error("Failed to fetch inspection status", err);
        } finally {
            setLoadingInspection(prev => ({ ...prev, [poId]: false }));
        }
    };

    // ── Manual Tally sync for a specific inspection (purchase invoice) ─────────
    const handleSyncToTallyInViewPo = async (inspectionId, poId) => {
        setSyncingTallyInViewPo(prev => ({ ...prev, [inspectionId]: 'syncing' }));
        try {
            const res = await fetch(`${API_URL}purchase-invoices/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ inspectionId }),
            });
            const result = await res.json();
            if (result.error) throw new Error(result.message || 'Tally sync failed');

            // Update the flag on the in-memory inspection record
            setSyncingTallyInViewPo(prev => ({ ...prev, [inspectionId]: 'done' }));
            setInspectionData(prev => {
                const poData = prev[poId];
                if (!poData) return prev;
                return {
                    ...prev,
                    [poId]: {
                        ...poData,
                        inspections: poData.inspections.map(insp =>
                            insp._id === inspectionId
                                ? { ...insp, tallyPurchaseSyncPending: false }
                                : insp
                        ),
                    },
                };
            });
        } catch (err) {
            setSyncingTallyInViewPo(prev => ({ ...prev, [inspectionId]: 'error' }));
            alert(`Tally Sync Failed: ${err.message}`);
        }
    };

    const handleCreateDebitNoteClick = (po) => {
        const inspection = inspectionData[po._id];

        const rejectedItems = (inspection?.purchaseOrder?.lineItems || [])
            .filter(item => {
                const summary = inspection?.lineItemSummary?.[item._id];
                return summary && summary.totalRejected > 0;
            })
            .map(item => {
                const summary = inspection?.lineItemSummary?.[item._id];
                const reasons = [];
                inspection?.inspections?.forEach(insp => {
                    const inspItem = insp.lineItems?.find(li =>
                        (li.model?._id || li.model) === (item.model?._id || item.model)
                    );
                    if (inspItem && inspItem.rejectedQuantity > 0 && inspItem.rejectionReason) {
                        if (!reasons.includes(inspItem.rejectionReason)) {
                            reasons.push(inspItem.rejectionReason);
                        }
                    }
                });

                return {
                    lineItemId: item?._id,
                    model: item?.model?._id || item?.model,
                    modelName: item?.model?.modelName || 'Unknown Model',
                    maxWrong: summary.totalWrong || 0,
                    maxDamaged: summary.totalDamaged || 0,
                    maxShortSupply: summary.totalShortSupply || 0,
                    selected: true,
                    wrongQuantity: summary.totalWrong || 0,
                    damagedQuantity: summary.totalDamaged || 0,
                    shortSupply: summary.totalShortSupply || 0,
                    perUnitPurchasePrice: item?.perUnitPurchasePrice,
                    gst: item?.gst || 18,
                    baseRejectionReason: reasons.join(', ') || ''
                };
            });

        setDebitNoteModal({ isOpen: true, po });
        setDebitNoteErrors({});
        setDebitNoteForm({
            invoiceNo: '',
            otherReferences: '',
            billingAddress: '',
            billingLocation: '',
            billingPin: '',
            shippingAddress: '',
            shippingLocation: '',
            shippingPin: '',
            lineItems: rejectedItems
        });
    };

    const submitDebitNote = async () => {
        const po = debitNoteModal.po;
        if (!po) return;

        // ── Client-side validation ────────────────────────────────────────────
        const errs = {};
        if (!debitNoteForm.billingAddress?.trim()) errs.billingAddress = 'Billing address is required';
        if (!debitNoteForm.billingLocation?.trim()) errs.billingLocation = 'Billing location is required';
        if (!debitNoteForm.billingPin?.trim()) errs.billingPin = 'Billing PIN is required';
        if (!debitNoteForm.shippingAddress?.trim()) errs.shippingAddress = 'Shipping address is required';
        if (!debitNoteForm.shippingLocation?.trim()) errs.shippingLocation = 'Shipping location is required';
        if (!debitNoteForm.shippingPin?.trim()) errs.shippingPin = 'Shipping PIN is required';
        if (!debitNoteForm.invoiceNo?.trim()) errs.invoiceNo = 'Invoice number is required';
        if (Object.keys(errs).length > 0) {
            setDebitNoteErrors(errs);
            return;
        }
        setDebitNoteErrors({});

        setSubmittingDebitNote(true);
        try {
            const selectedItems = debitNoteForm.lineItems.filter(item => item.selected);
            if (selectedItems.length === 0) {
                throw new Error("Please select at least one item to create a Debit Note.");
            }

            const payloadItems = selectedItems.map(item => {
                const totalQuantity = (item.wrongQuantity || 0) + (item.damagedQuantity || 0) + (item.shortSupply || 0);

                let combinedReason = [];
                if (item.wrongQuantity > 0) combinedReason.push(`Wrong: ${item.wrongQuantity}`);
                if (item.damagedQuantity > 0) combinedReason.push(`Damaged: ${item.damagedQuantity}`);
                if (item.shortSupply > 0) combinedReason.push(`Short Supply: ${item.shortSupply}`);

                let finalReason = combinedReason.join(', ');
                if (item.baseRejectionReason) finalReason += ` | Notes: ${item.baseRejectionReason}`;

                return {
                    lineItemId: item.lineItemId,
                    model: item.model,
                    quantity: totalQuantity,
                    rejectedQuantity: totalQuantity,
                    wrongQuantity: item.wrongQuantity,
                    damagedQuantity: item.damagedQuantity,
                    shortSupply: item.shortSupply,
                    perUnitPurchasePrice: item.perUnitPurchasePrice,
                    gst: item.gst,
                    rejectionReason: finalReason
                };
            }).filter(item => item.quantity > 0);

            if (payloadItems.length === 0) {
                throw new Error("Please select a quantity greater than zero for at least one item.");
            }

            const payload = {
                purchaseOrder: po._id,
                shippingAddress: debitNoteForm.shippingAddress || po.deliveryAddress || '',
                shippingLocation: debitNoteForm.shippingLocation || '',
                shippingPin: debitNoteForm.shippingPin || '',
                billingAddress: debitNoteForm.billingAddress || po.customerAddress || '',
                billingLocation: debitNoteForm.billingLocation || '',
                billingPin: debitNoteForm.billingPin || '',
                invoiceNo: debitNoteForm.invoiceNo,
                otherReferences: debitNoteForm.otherReferences,
                lineItems: payloadItems
            };

            const res = await fetch(`${API_URL}debit-notes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const result = await res.json();
            if (!res.ok || result.error) throw new Error(result.message || "Failed to create Debit Note");

            setDebitNoteModal({ isOpen: false, po: null });
            alert('Debit Note Created Successfully!');
        } catch (err) {
            alert(err.message || 'Error creating Debit Note');
        } finally {
            setSubmittingDebitNote(false);
        }
    };

    const isPoClosed = (po) => po?.status === 'closed';

    const openEditModal = (po) => {
        setSelectedEditPo(po);
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setSelectedEditPo(null);
    };

    const openPurchaseOrder = async (po) => {
        const res = await fetch(`${API_URL}purchase-orders/${po._id}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: 'open' }),
        });
        const result = await res.json();
        if (!res.ok || result.error) {
            throw new Error(result.message || 'Failed to open purchase order');
        }
        return result.data || { ...po, status: 'open' };
    };

    const handleOpenPoForEdit = async (po) => {
        if (openingPoId) return;

        const confirmed = window.confirm(
            `PO ${po.poNumber} is closed. Open it to make changes?`
        );
        if (!confirmed) return;

        try {
            setOpeningPoId(po._id);
            const updatedPo = await openPurchaseOrder(po);
            setData(prev => prev.map(item => (item._id === po._id ? { ...item, ...updatedPo } : item)));
            openEditModal({ ...po, ...updatedPo, status: 'open' });
        } catch (err) {
            alert(err.message || 'Failed to open purchase order');
        } finally {
            setOpeningPoId(null);
        }
    };

    const handleEditPoClick = (po) => {
        if (isPoClosed(po)) {
            handleOpenPoForEdit(po);
            return;
        }
        openEditModal(po);
    };

    const handleEditSuccess = (updatedPo) => {
        if (updatedPo?._id) {
            setData(prev => prev.map(po => (po._id === updatedPo._id ? updatedPo : po)));
        } else {
            fetchPurchaseOrders(searchQuery);
        }
    };

    const downloadFile = async (filePath) => {
        try {
            const url = `${API_URL}file/serve`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ filePath })
            });
            if (!response.ok) return;
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filePath.split('/').pop() || 'invoice';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Invoice download failed:', err);
            alert('Failed to download the invoice. Please try again.');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    };

    const fmtAmount = v => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const totalPOValue = (po) =>
        po?.totalValue ?? po?.lineItems?.reduce((sum, item) => sum + (item.totalPurchasePrice || 0), 0) ?? 0;

    // ── Number → Indian Words ──────────────────────────────────────────────────
    const numberToWords = (amount) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

        const toWords = (n) => {
            if (n === 0) return '';
            if (n < 20) return ones[n] + ' ';
            if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
            if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + toWords(n % 100);
            if (n < 100000) return toWords(Math.floor(n / 1000)) + 'Thousand ' + toWords(n % 1000);
            if (n < 10000000) return toWords(Math.floor(n / 100000)) + 'Lakh ' + toWords(n % 100000);
            return toWords(Math.floor(n / 10000000)) + 'Crore ' + toWords(n % 10000000);
        };

        const n = Math.round(amount);
        const paise = Math.round((amount - n) * 100);
        let result = (toWords(n) || 'Zero ').trim() + ' Rupees';
        if (paise > 0) result += ' and ' + toWords(paise).trim() + ' Paise';
        return result + ' Only';
    };

    // --- PDF GENERATION LOGIC ---
    const handleDownloadPDF = (po) => {
        const doc = new jsPDF();
        const companyLogo = Logo;

        if (companyLogo) {
            doc.addImage(companyLogo, 'PNG', 14, 10, 40, 20);
        } else {
            doc.setFontSize(18).setFont("helvetica", "bold").setTextColor(37, 99, 235);
            doc.text("GREENERGY SOLUTION", 14, 20);
        }

        doc.setFontSize(20).setTextColor(17, 24, 39).setFont("helvetica", "bold");
        doc.text("PURCHASE ORDER", 196, 20, { align: "right" });

        doc.setFontSize(10).setFont("helvetica", "normal").setTextColor(100);
        doc.text(`PO Number: ${po.poNumber}`, 196, 28, { align: "right" });
        doc.text(`Date: ${formatDate(po.orderPlacedDate)}`, 196, 33, { align: "right" });

        doc.setDrawColor(229, 231, 235).line(14, 40, 196, 40);

        // Address Section
        doc.setFontSize(11).setTextColor(0).setFont("helvetica", "bold").text("Billing Address:", 14, 48);
        doc.setFont("helvetica", "normal").setFontSize(9);
        const billingLines = doc.splitTextToSize(po.customerAddress || 'N/A', 85);
        doc.text(billingLines, 14, 55);

        doc.setFontSize(11).setFont("helvetica", "bold").text("Shipping Address:", 110, 48);
        doc.setFont("helvetica", "normal").setFontSize(9);
        const shippingLines = doc.splitTextToSize(po.deliveryAddress || 'N/A', 80);
        doc.text(shippingLines, 110, 55);

        const addrEndY = Math.max(55 + billingLines.length * 4.5, 55 + shippingLines.length * 4.5) + 6;

        doc.setFontSize(11).setFont("helvetica", "bold").text("Supplier:", 14, addrEndY);
        doc.setFont("helvetica", "normal").setFontSize(9);
        const rawVendorAddr = po.vendor?.address;
        const vendorAddr = Array.isArray(rawVendorAddr) ? rawVendorAddr[0] : rawVendorAddr;
        const vendorAddrText = typeof vendorAddr === 'string'
            ? vendorAddr
            : [vendorAddr?.addressLine1, vendorAddr?.addressLine2, vendorAddr?.city, vendorAddr?.state, vendorAddr?.country, vendorAddr?.pincode]
                .filter(Boolean).join(', ');
        const supplierLines = [
            po.vendor?.vendorName || 'N/A',
            ...doc.splitTextToSize(vendorAddrText || 'N/A', 120),
            `GSTIN: ${po.vendor?.gstin || 'N/A'}`,
            `Email: ${po.vendor?.vendorEmail || 'N/A'}`,
            `Phone: ${po.vendor?.vendorPhone || 'N/A'}`
        ];
        doc.text(supplierLines, 14, addrEndY + 6);

        // Table
        const tableRows = po.lineItems?.map((item, index) => [
            index + 1,
            { content: `${item.model?.modelName}\nPart Code: ${item.model?.partCode}`, styles: { fontStyle: 'bold' } },
            item.model?.uom || 'Nos',
            item.quantity,
            item.perUnitPurchasePrice?.toLocaleString('en-IN'),
            item.gst ? `${item.gst}%` : '18%',
            item.totalPurchasePrice?.toLocaleString('en-IN'),
        ]) || [];

        autoTable(doc, {
            startY: Math.max(addrEndY + 28, addrEndY + 10 + supplierLines.length * 4),
            head: [['#', 'Item Description', 'UOM', 'Qty', 'Unit Rate', 'GST %', 'Total Amount']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [17, 24, 39], fontSize: 10, halign: 'center' },
            styles: { fontSize: 9, cellPadding: 3 },
        });

        let finalY = doc.lastAutoTable.finalY + 10;

        // Tax summary rows
        const taxableAmt = po.totalTaxableValue ?? po.lineItems?.reduce((s, i) => s + (i.taxableValue || (i.quantity * i.perUnitPurchasePrice) || 0), 0) ?? 0;
        const taxAmt = po.totalTaxValue ?? po.lineItems?.reduce((s, i) => s + (i.taxValue || 0), 0) ?? 0;
        const grandTotal = po.totalValue ?? totalPOValue(po);

        const summaryRows = [
            ['Taxable Value (A)', `Rs. ${taxableAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
            ['Total Tax / GST (B)', `Rs. ${taxAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
            ['Grand Total (A + B)', `Rs. ${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`],
        ];

        autoTable(doc, {
            startY: finalY,
            head: [['Description', 'Amount']],
            body: summaryRows,
            theme: 'plain',
            headStyles: { fillColor: [17, 24, 39], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            columnStyles: { 0: { cellWidth: 100 }, 1: { halign: 'right', fontStyle: 'bold' } },
            didParseCell: (data) => {
                if (data.row.index === 2) data.cell.styles.fontStyle = 'bold';
            }
        });

        finalY = doc.lastAutoTable.finalY + 6;

        // Amount in words
        doc.setFillColor(239, 246, 255);
        doc.rect(14, finalY, 182, 10, 'F');
        doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(30, 64, 175);
        doc.text('Amount in Words:', 17, finalY + 6.5);
        doc.setFont('helvetica', 'normal').setTextColor(17, 24, 39);
        const amtWords = doc.splitTextToSize(numberToWords(grandTotal), 130);
        doc.text(amtWords, 55, finalY + 6.5);

        finalY += 10 + amtWords.length * 4.5 + 4;

        // --- ADDED: Terms & Conditions in PDF ---
        doc.setFontSize(11).setFont("helvetica", "bold").text("Terms & Conditions:", 14, finalY);
        doc.setFontSize(9).setFont("helvetica", "normal");
        const termsData = [
            `Price Basis: ${po.priceValidity || 'N/A'}`,
            `Payment Terms: ${po.paymentTerms || 'N/A'}`,
            `Packing: ${po.packing || 'N/A'}`,
            `Freight: ${po.freight || 'N/A'}`,
            `Transit Insurance: ${po.transitInsurance || 'N/A'}`
        ];
        doc.text(termsData, 14, finalY + 6);

        // Taxation in PDF is now line-item wise

        // Signature
        doc.line(140, finalY + 40, 196, finalY + 40);
        doc.text("Authorized Signatory", 168, finalY + 45, { align: 'center' });

        doc.save(`PO_${po.poNumber}.pdf`);
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen gap-3">
            <Spinner color="primary" />
            <span className="text-gray-600 font-semibold">Loading Purchase Orders...</span>
        </div>
    );

    if (error) return <div className="p-6"><Alert color="danger">Failed to load: {error}</Alert></div>;

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8 lg:px-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6 pb-5 border-b-2 border-gray-200">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Purchase Orders</h1>
                    <p className="text-gray-500 text-sm mt-1">Track and manage all issued purchase orders</p>
                </div>
                <span className="bg-blue-700 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
                    {data.length} Orders
                </span>
            </div>

            {/* ── Search Bar ─────────────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search by PO Number or Vendor Name…"
                        className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>

                {loading && searchQuery && (
                    <Spinner size="sm" color="primary" />
                )}
            </div>

            <div className="flex flex-col gap-6">
                {data.map((po) => (
                    <div key={po._id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                        <div className="bg-gray-900 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <ShoppingCart size={20} className="text-green-400" />
                                <span className="text-white font-bold text-lg tracking-tight">{po.poNumber}</span>
                                <button onClick={() => handleDownloadPDF(po)} className="ml-2 flex items-center gap-2 bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all">
                                    <FileText size={14} className="text-blue-400" /> Download PDF
                                </button>
                                {isPoClosed(po) ? (
                                    <button
                                        onClick={() => handleOpenPoForEdit(po)}
                                        disabled={openingPoId === po._id}
                                        className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 border border-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm"
                                    >
                                        {openingPoId === po._id ? (
                                            <Spinner size="sm" className="text-white" />
                                        ) : (
                                            <LockOpen size={14} className="text-white" />
                                        )}
                                        Open & Edit PO
                                    </button>
                                ) : (
                                    <button onClick={() => handleEditPoClick(po)} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 border border-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm">
                                        <Pencil size={14} className="text-white" /> Edit PO
                                    </button>
                                )}
                                {
                                    po?.status !== 'closed' && userDetails?.role === 'store_manager' ?
                                        <button onClick={() => navigate(`/purchase/material-inspection/${po._id}`)} className="flex items-center gap-2 bg-green-500 hover:bg-green-600 border border-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm">
                                            <Package size={14} className="text-white" /> Inspect Materials
                                        </button> : <button onClick={() => toggleInspectionStatus(po._id)} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 border border-yellow-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all shadow-sm">
                                            {loadingInspection[po._id] ? <Spinner size="sm" className="text-white" /> : <Package size={14} className="text-white" />} Inspection Status
                                        </button>
                                }

                            </div>
                            <div className="flex gap-6 text-gray-400 text-sm">
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                                            isPoClosed(po)
                                                ? 'bg-red-500 text-white'
                                                : 'bg-green-500 text-white'
                                        }`}
                                    >
                                        {isPoClosed(po) ? 'Closed' : 'Open'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2"><Calendar size={14} /> <span>Date:</span> <span className="text-white">{formatDate(po.orderPlacedDate)}</span></div>
                            </div>
                        </div>

                        {!expandedInspectionRows[po._id] ? (
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                                    {/* Billing Address */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-2"><Mail size={13} /> Billing Address</p>
                                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                                            <p className="text-sm text-blue-900 whitespace-pre-line mb-0">{po.customerAddress || '—'}</p>
                                        </div>
                                    </div>
                                    {/* Shipping Address */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-2"><Truck size={13} /> Shipping Address</p>
                                        <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                                            <p className="text-sm text-green-900 whitespace-pre-line mb-0">{po.deliveryAddress || '—'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Vendor Info */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><Building2 size={13} /> Vendor</p>
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                            <p className="font-bold text-gray-900 text-base mb-1">{po.vendor?.vendorName}</p>
                                            <p className="text-gray-500 text-sm mb-1 break-all">{po.vendor?.vendorEmail}</p>
                                            <span className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-3 py-1 rounded-md font-mono mt-2">
                                                <CreditCard size={11} /> {po.vendor?.gstin || 'No GSTIN'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Terms & Conditions - REPLACES QUOTATION REF */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><ShieldCheck size={13} /> Terms & Conditions</p>
                                        <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-xs space-y-2">
                                            <div className="flex justify-between"><span className="text-gray-500">Validity:</span><span className="font-bold text-orange-800">{po.priceValidity}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Payment:</span><span className="font-bold text-orange-800">{po.paymentTerms}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Packing:</span><span className="font-bold text-orange-800">{po.packing}</span></div>
                                            <div className="flex justify-between"><span className="text-gray-500">Logistics:</span><span className="font-bold text-orange-800">{po.freight} / {po.transitInsurance}</span></div>
                                        </div>
                                    </div>

                                    {/* Taxation Details */}
                                    <div>
                                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1 mb-3"><Receipt size={13} /> Taxation & Value</p>
                                        <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-2 text-xs">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Taxable Value:</span>
                                                <span className="font-bold text-gray-800">₹ {(po.totalTaxableValue ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Total Tax (GST):</span>
                                                <span className="font-bold text-amber-600">₹ {(po.totalTaxValue ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                            <div className="flex justify-between border-t border-green-200 pt-2 mt-1">
                                                <span className="font-bold text-green-700 uppercase tracking-wider">Grand Total:</span>
                                                <span className="text-xl font-extrabold text-green-700">₹ {(po.totalValue ?? totalPOValue(po)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-dashed border-gray-200 my-5" />

                                {/* Line Items Toggle */}
                                <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleRow(po._id)}>
                                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-2 mb-0">
                                        <Package size={13} /> Line Items <span className="bg-gray-900 text-white text-xs font-bold px-2 py-0.5 rounded-full">{po.lineItems?.length || 0}</span>
                                    </p>
                                    <button className="flex items-center gap-2 border border-gray-300 bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all">
                                        {expandedRows[po._id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expandedRows[po._id] ? 'Hide' : 'View All'}
                                    </button>
                                </div>

                                {expandedRows[po._id] && (
                                    <div className="mt-4 rounded-xl overflow-hidden border border-gray-200 overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-wider">
                                                <tr>{['#', 'Model', 'HSN', 'UOM', 'Qty', 'Inspected', 'Rate', 'GST %', 'Taxable Value', 'Tax Value', 'Total', 'Required Date'].map(h => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {po.lineItems?.map((item, idx) => (
                                                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 text-gray-400">{idx + 1}</td>
                                                        <td className="px-4 py-3"><p className="font-bold text-gray-900 mb-0">{item.model?.modelName}</p><p className="text-xs text-gray-400 font-mono">{item.model?.partCode}</p></td>
                                                        <td className="px-4 py-3 text-xs font-mono">{item.model?.HSNNumber || '—'}</td>
                                                        <td className="px-4 py-3"><span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-1 rounded uppercase">{item.model?.uom}</span></td>
                                                        <td className="px-4 py-3 font-bold">{item?.quantity}</td>
                                                        <td className="px-4 py-3 font-bold">{item?.inspectedQuantity}</td>
                                                        <td className="px-4 py-3 text-gray-600">{item?.perUnitPurchasePrice?.toLocaleString('en-IN')}</td>
                                                        <td className="px-4 py-3 text-gray-600">{item?.gst || '18'}%</td>
                                                        <td className="px-4 py-3 text-gray-700 font-semibold">{(item?.taxableValue ?? (item.quantity * item.perUnitPurchasePrice))?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="px-4 py-3 text-amber-600 font-semibold">{(item?.taxValue ?? 0)?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="px-4 py-3 text-gray-900 font-bold">{item?.totalPurchasePrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                                        <td className="px-4 py-3 text-gray-500">{formatDate(item.requiredDateOfMaterial)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                            </div>
                        ) : (
                            expandedInspectionRows[po._id] && inspectionData[po._id] && (
                                <div className="p-6 bg-slate-50 border-y border-slate-200">

                                    {/* Header row */}
                                    <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
                                        <div>
                                            <h4 className="text-sm font-bold text-gray-800 uppercase tracking-widest mb-0 flex items-center gap-2">
                                                <Package size={15} className="text-orange-500" />
                                                Inspection Records
                                                <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                                    {inspectionData[po._id].totalInvoices} Invoice{inspectionData[po._id].totalInvoices !== 1 ? 's' : ''}
                                                </span>
                                            </h4>
                                        </div>
                                        <button
                                            onClick={() => handleCreateDebitNoteClick(po)}
                                            className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all shadow-sm active:scale-95 whitespace-nowrap"
                                        >
                                            Create Debit Note for Rejected Items
                                        </button>
                                    </div>

                                    {/* Per-Invoice Cards */}
                                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-3">Invoice-wise Inspection Records</p>
                                    <div className="flex flex-col gap-4">
                                        {inspectionData[po._id].inspections.map((insp, invIdx) => (
                                            <div key={insp._id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                                                {/* Invoice header */}
                                                <div className="bg-slate-800 px-4 py-3 flex flex-wrap gap-3 items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">INV {invIdx + 1}</span>
                                                        <span className="text-white font-bold text-sm">{insp.invoiceNumber}</span>
                                                        <span className="text-slate-400 text-xs">{formatDate(insp.invoiceDate)}</span>
                                                        {insp.invoiceDocumentPath && (
                                                            <button
                                                                onClick={() => downloadFile(insp.invoiceDocumentPath)}
                                                                title="Download Invoice Document"
                                                                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold px-2.5 py-1 rounded-md transition-all active:scale-95"
                                                            >
                                                                <Download size={11} /> Invoice
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        {/* ── Tally Sync Indicator ── */}
                                                        {(() => {
                                                            const syncState = syncingTallyInViewPo[insp._id];
                                                            const isPending = insp.tallyPurchaseSyncPending !== false;
                                                            const isSyncing = syncState === 'syncing';
                                                            if (!isPending) {
                                                                return (
                                                                    <span className="flex items-center gap-1.5 bg-green-500 bg-opacity-20 border border-green-500 border-opacity-40 text-green-300 text-[10px] font-bold px-2.5 py-1 rounded-full">
                                                                        <CheckCircle2 size={10} /> Synced to Tally
                                                                    </span>
                                                                );
                                                            }
                                                            return (
                                                                <button
                                                                    onClick={() => handleSyncToTallyInViewPo(insp._id, po._id)}
                                                                    disabled={isSyncing}
                                                                    title="Purchase invoice not yet synced to Tally — click to retry"
                                                                    className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all active:scale-95"
                                                                >
                                                                    {isSyncing
                                                                        ? <Spinner size="sm" style={{ width: '10px', height: '10px' }} />
                                                                        : <RotateCcw size={10} />
                                                                    }
                                                                    {isSyncing ? 'Syncing…' : 'Sync to Tally'}
                                                                </button>
                                                            );
                                                        })()}
                                                        {/* Received / Created By info */}
                                                        <div className="flex flex-wrap gap-4 text-xs text-slate-300">
                                                            {insp.vehicleNumber && <span><span className="text-slate-500">Vehicle:</span> {insp.vehicleNumber}</span>}
                                                            {insp.freight > 0 && <span><span className="text-slate-500">Freight:</span> ₹{insp.freight.toLocaleString('en-IN')}</span>}
                                                            <span><span className="text-slate-500">Received:</span> {formatDate(insp.receivedDate)}</span>
                                                            <span><span className="text-slate-500">By:</span> {insp.createdBy?.name}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Transporter Details */}
                                                {insp.transporterDetails && insp.transporterDetails.length > 0 && (
                                                    <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                                                        <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Transporter Details</h5>
                                                        <div className="flex flex-wrap gap-3">
                                                            {insp.transporterDetails.map((tp, tpIdx) => (
                                                                <div key={tp._id || tpIdx} className="bg-white border border-slate-200 rounded-lg p-3 text-xs w-full sm:w-auto min-w-[280px]">
                                                                    <div className="flex justify-between items-start mb-2 border-b border-slate-100 pb-2">
                                                                        <div>
                                                                            <p className="font-bold text-slate-800 mb-0">{tp.transporterName || '—'}</p>
                                                                            {tp.gstin && <p className="text-[10px] font-mono text-slate-500 mb-0">GSTIN: {tp.gstin}</p>}
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <p className="font-semibold text-slate-700 mb-0">{tp.invoiceNo || '—'}</p>
                                                                            <p className="text-[10px] text-slate-500 mb-0">{formatDate(tp.invoiceDate)}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                                                                        <div>
                                                                            <span className="text-slate-500 block">Taxable Value</span>
                                                                            <span className="font-semibold text-slate-700">₹ {(tp.taxableValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <span className="text-slate-500 block">Total Invoice Value</span>
                                                                            <span className="font-bold text-indigo-700">₹ {(tp.totalInvoiceValue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                                                        </div>
                                                                        {tp.gstin && tp.gstSlab > 0 && (
                                                                            <div className="col-span-2 bg-slate-50 p-1.5 rounded mt-1 flex flex-wrap gap-2 justify-between">
                                                                                <span className="text-slate-500 font-semibold">GST ({tp.gstSlab}%)</span>
                                                                                <div className="font-semibold text-slate-700 text-right">
                                                                                    {tp.integratedTax > 0 && <span>IGST: ₹{tp.integratedTax} </span>}
                                                                                    {tp.centralTax > 0 && <span>CGST: ₹{tp.centralTax} </span>}
                                                                                    {tp.stateTax > 0 && <span>SGST: ₹{tp.stateTax}</span>}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Invoice line items */}
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider border-b border-slate-100">
                                                            <tr>
                                                                <th className="px-4 py-2 text-left font-semibold">#</th>
                                                                <th className="px-4 py-2 text-left font-semibold">Model</th>
                                                                <th className="px-4 py-2 text-center font-semibold">Status</th>
                                                                <th className="px-4 py-2 text-center font-semibold">Inspected</th>
                                                                <th className="px-4 py-2 text-center font-semibold text-green-700">Approved</th>
                                                                <th className="px-4 py-2 text-center font-semibold text-red-600">Wrong</th>
                                                                <th className="px-4 py-2 text-center font-semibold text-red-600">Damaged</th>
                                                                <th className="px-4 py-2 text-center font-semibold text-red-600">Short Supply</th>
                                                                <th className="px-4 py-2 text-right font-semibold">Rate (₹)</th>
                                                                <th className="px-4 py-2 text-right font-semibold">Amount (₹)</th>
                                                                <th className="px-4 py-2 text-center font-semibold">GST %</th>
                                                                <th className="px-4 py-2 text-right font-semibold">GST Amount (₹)</th>
                                                                <th className="px-4 py-2 text-left font-semibold">Reason</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-100">
                                                            {insp.lineItems.map((li, liIdx) => {
                                                                const invItems = insp.purchaseInvoice?.items || [];
                                                                const modelId = li.model?._id || li.model;
                                                                const invItem =
                                                                    invItems.find(it => li.poLineItemId && String(it.poLineItemId) === String(li.poLineItemId)) ||
                                                                    invItems.find(it => modelId && String(it.model) === String(modelId));
                                                                const hasCgstSgst = invItem && (invItem.cgstAmount != null || invItem.sgstAmount != null);
                                                                const hasIgst = invItem && invItem.igstAmount != null;
                                                                return (
                                                                <tr key={li._id} className="hover:bg-slate-50 transition-colors">
                                                                    <td className="px-4 py-3 text-gray-400">{liIdx + 1}</td>
                                                                    <td className="px-4 py-3 min-w-[160px]">
                                                                        <p className="font-bold text-gray-900 mb-0">{li.model?.modelName || '—'}</p>
                                                                        <p className="text-xs text-gray-400 font-mono">{li.model?.partCode}</p>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center">
                                                                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase ${li.inspectionStatus === 'approved' ? 'bg-green-100 text-green-700' :
                                                                            li.inspectionStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                                                                                'bg-yellow-100 text-yellow-700'
                                                                            }`}>{li.inspectionStatus}</span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center font-bold text-blue-700">{li.inspectedQuantity}</td>
                                                                    <td className="px-4 py-3 text-center font-bold text-green-700">{li.approvedQuantity}</td>
                                                                    <td className="px-4 py-3 text-center font-bold text-red-600">{li.wrongQuantity || 0}</td>
                                                                    <td className="px-4 py-3 text-center font-bold text-red-600">{li.damagedQuantity || 0}</td>
                                                                    <td className="px-4 py-3 text-center font-bold text-red-600">{li.shortSupply || 0}</td>
                                                                    <td className="px-4 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">
                                                                        {invItem?.rate != null ? fmtAmount(invItem.rate) : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                                                        {invItem?.amount != null ? fmtAmount(invItem.amount) : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-center font-semibold text-gray-700">
                                                                        {invItem?.taxRate != null ? `${invItem.taxRate}%` : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right text-xs whitespace-nowrap">
                                                                        {hasCgstSgst ? (
                                                                            <>
                                                                                <p className="mb-0 text-gray-700"><span className="text-gray-400">CGST:</span> {fmtAmount(invItem.cgstAmount)}</p>
                                                                                <p className="mb-0 text-gray-700"><span className="text-gray-400">SGST:</span> {fmtAmount(invItem.sgstAmount)}</p>
                                                                                <p className="mb-0 font-bold text-gray-900">{fmtAmount((invItem.cgstAmount || 0) + (invItem.sgstAmount || 0))}</p>
                                                                            </>
                                                                        ) : hasIgst ? (
                                                                            <p className="mb-0 font-bold text-gray-900"><span className="font-normal text-gray-400">IGST:</span> {fmtAmount(invItem.igstAmount)}</p>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-xs text-gray-500 italic">{li.rejectionReason || '—'}</td>
                                                                </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                {insp.purchaseInvoice && (() => {
                                                    const pi = insp.purchaseInvoice;
                                                    const items = pi.items || [];
                                                    const taxable = pi.purchaseLedgerAmount ?? items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
                                                    const gstTotal = items.reduce(
                                                        (s, it) => s + (Number(it.cgstAmount) || 0) + (Number(it.sgstAmount) || 0) + (Number(it.igstAmount) || 0),
                                                        0
                                                    );
                                                    const invoiceTotal = pi.totalAmount ?? taxable + gstTotal;
                                                    return (
                                                        <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-end gap-6">
                                                            <div className="text-right">
                                                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">Taxable Value</p>
                                                                <p className="text-sm font-bold text-slate-800 mb-0">₹ {fmtAmount(taxable)}</p>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-0.5">Total GST</p>
                                                                <p className="text-sm font-bold text-amber-600 mb-0">₹ {fmtAmount(gstTotal)}</p>
                                                            </div>
                                                            <div className="text-right border-l border-slate-300 pl-6">
                                                                <p className="text-[10px] font-bold text-green-700 uppercase tracking-widest mb-0.5">Total Invoice Value</p>
                                                                <p className="text-lg font-extrabold text-green-700 mb-0">₹ {fmtAmount(invoiceTotal)}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ))}
                                    </div>

                                </div>
                            )
                        )}

                        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <User size={14} className="text-blue-500" />
                                <span>Created by</span> <span className="font-semibold text-gray-800">{po.createdBy?.name}</span>
                                <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-2 py-0.5 rounded uppercase">{po.createdBy?.role?.replace(/_/g, ' ')}</span>
                            </div>
                            <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">Created: {formatDate(po.createdAt)}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Debit Note Modal */}
            {debitNoteModal.isOpen && (
                <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 backdrop-blur-sm bg-gray-900/40">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden max-h-[90vh] flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <Receipt size={18} className="text-red-500" />
                                Create Debit Note
                            </h2>
                            <button onClick={() => setDebitNoteModal({ isOpen: false, po: null })} className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors border-0 bg-transparent">
                                <AlertTriangle size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Invoice Number <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.invoiceNo ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                    value={debitNoteForm.invoiceNo}
                                    placeholder="Enter associated vendor invoice no"
                                    onChange={(e) => {
                                        setDebitNoteForm(prev => ({ ...prev, invoiceNo: e.target.value }));
                                        if (debitNoteErrors.invoiceNo) setDebitNoteErrors(prev => ({ ...prev, invoiceNo: '' }));
                                    }}
                                />
                                {debitNoteErrors.invoiceNo && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.invoiceNo}</p>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Billing Address <span className="text-red-500">*</span></label>
                                    <textarea
                                        className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.billingAddress ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                        rows="3"
                                        value={debitNoteForm.billingAddress}
                                        placeholder="Enter billing address"
                                        onChange={(e) => {
                                            setDebitNoteForm(prev => ({ ...prev, billingAddress: e.target.value }));
                                            if (debitNoteErrors.billingAddress) setDebitNoteErrors(prev => ({ ...prev, billingAddress: '' }));
                                        }}
                                    />
                                    {debitNoteErrors.billingAddress && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.billingAddress}</p>}
                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <div>
                                            <input
                                                type="text"
                                                className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.billingLocation ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                                value={debitNoteForm.billingLocation}
                                                placeholder="Location *"
                                                onChange={(e) => {
                                                    setDebitNoteForm(prev => ({ ...prev, billingLocation: e.target.value }));
                                                    if (debitNoteErrors.billingLocation) setDebitNoteErrors(prev => ({ ...prev, billingLocation: '' }));
                                                }}
                                            />
                                            {debitNoteErrors.billingLocation && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.billingLocation}</p>}
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.billingPin ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                                value={debitNoteForm.billingPin}
                                                placeholder="PIN Code *"
                                                onChange={(e) => {
                                                    setDebitNoteForm(prev => ({ ...prev, billingPin: e.target.value }));
                                                    if (debitNoteErrors.billingPin) setDebitNoteErrors(prev => ({ ...prev, billingPin: '' }));
                                                }}
                                            />
                                            {debitNoteErrors.billingPin && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.billingPin}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Shipping Address <span className="text-red-500">*</span></label>
                                    <textarea
                                        className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.shippingAddress ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                        rows="3"
                                        value={debitNoteForm.shippingAddress}
                                        placeholder="Enter shipping address"
                                        onChange={(e) => {
                                            setDebitNoteForm(prev => ({ ...prev, shippingAddress: e.target.value }));
                                            if (debitNoteErrors.shippingAddress) setDebitNoteErrors(prev => ({ ...prev, shippingAddress: '' }));
                                        }}
                                    />
                                    {debitNoteErrors.shippingAddress && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.shippingAddress}</p>}
                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        <div>
                                            <input
                                                type="text"
                                                className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.shippingLocation ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                                value={debitNoteForm.shippingLocation}
                                                placeholder="Location *"
                                                onChange={(e) => {
                                                    setDebitNoteForm(prev => ({ ...prev, shippingLocation: e.target.value }));
                                                    if (debitNoteErrors.shippingLocation) setDebitNoteErrors(prev => ({ ...prev, shippingLocation: '' }));
                                                }}
                                            />
                                            {debitNoteErrors.shippingLocation && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.shippingLocation}</p>}
                                        </div>
                                        <div>
                                            <input
                                                type="text"
                                                className={`w-full px-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 outline-none text-sm ${debitNoteErrors.shippingPin ? 'border-red-400 bg-red-50 focus:ring-red-400' : 'border-gray-200 focus:ring-blue-500'}`}
                                                value={debitNoteForm.shippingPin}
                                                placeholder="PIN Code *"
                                                onChange={(e) => {
                                                    setDebitNoteForm(prev => ({ ...prev, shippingPin: e.target.value }));
                                                    if (debitNoteErrors.shippingPin) setDebitNoteErrors(prev => ({ ...prev, shippingPin: '' }));
                                                }}
                                            />
                                            {debitNoteErrors.shippingPin && <p className="text-xs text-red-500 mt-1 mb-0">{debitNoteErrors.shippingPin}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Other References</label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                    value={debitNoteForm.otherReferences}
                                    placeholder="Any additional references"
                                    onChange={(e) => setDebitNoteForm(prev => ({ ...prev, otherReferences: e.target.value }))}
                                />
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-bold text-gray-700 mb-2">Select Items for Debit Note <span className="text-red-500">*</span></label>
                                <div className="border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                            <tr>
                                                <th className="px-3 py-2 w-10"></th>
                                                <th className="px-3 py-2">Model</th>
                                                <th className="px-3 py-2 min-w-[80px]">Wrong</th>
                                                <th className="px-3 py-2 min-w-[80px]">Damaged</th>
                                                <th className="px-3 py-2 min-w-[80px]">Short</th>
                                                <th className="px-3 py-2 min-w-[60px]">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {debitNoteForm.lineItems?.map((item, index) => (
                                                <tr key={index} className="bg-white">
                                                    <td className="px-3 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.selected}
                                                            onChange={(e) => {
                                                                const newItems = [...debitNoteForm.lineItems];
                                                                newItems[index].selected = e.target.checked;
                                                                setDebitNoteForm({ ...debitNoteForm, lineItems: newItems });
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 font-medium">{item.modelName}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-400">Max: {item.maxWrong}</span>
                                                            <input
                                                                type="number"
                                                                className="w-full px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
                                                                value={item.wrongQuantity}
                                                                min={0}
                                                                max={item.maxWrong}
                                                                disabled={!item.selected || item.maxWrong === 0}
                                                                onChange={(e) => {
                                                                    const val = parseInt(e.target.value);
                                                                    const newItems = [...debitNoteForm.lineItems];
                                                                    newItems[index].wrongQuantity = isNaN(val) ? 0 : Math.min(Math.max(0, val), item.maxWrong);
                                                                    setDebitNoteForm({ ...debitNoteForm, lineItems: newItems });
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-400">Max: {item.maxDamaged}</span>
                                                            <input
                                                                type="number"
                                                                className="w-full px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
                                                                value={item.damagedQuantity}
                                                                min={0}
                                                                max={item.maxDamaged}
                                                                disabled={!item.selected || item.maxDamaged === 0}
                                                                onChange={(e) => {
                                                                    const val = parseInt(e.target.value);
                                                                    const newItems = [...debitNoteForm.lineItems];
                                                                    newItems[index].damagedQuantity = isNaN(val) ? 0 : Math.min(Math.max(0, val), item.maxDamaged);
                                                                    setDebitNoteForm({ ...debitNoteForm, lineItems: newItems });
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-400">Max: {item.maxShortSupply}</span>
                                                            <input
                                                                type="number"
                                                                className="w-full px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-blue-500 disabled:bg-gray-100"
                                                                value={item.shortSupply}
                                                                min={0}
                                                                max={item.maxShortSupply}
                                                                disabled={!item.selected || item.maxShortSupply === 0}
                                                                onChange={(e) => {
                                                                    const val = parseInt(e.target.value);
                                                                    const newItems = [...debitNoteForm.lineItems];
                                                                    newItems[index].shortSupply = isNaN(val) ? 0 : Math.min(Math.max(0, val), item.maxShortSupply);
                                                                    setDebitNoteForm({ ...debitNoteForm, lineItems: newItems });
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 font-bold text-center">
                                                        {(item.wrongQuantity || 0) + (item.damagedQuantity || 0) + (item.shortSupply || 0)}
                                                    </td>
                                                </tr>
                                            ))}
                                            {(!debitNoteForm.lineItems || debitNoteForm.lineItems.length === 0) && (
                                                <tr>
                                                    <td colSpan="6" className="px-3 py-4 text-center text-gray-500">No rejected items found.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-800 mt-4">
                                <p className="mb-0 font-semibold">Note: You can select which rejected items and quantities to include in this debit note.</p>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                            <button
                                onClick={() => setDebitNoteModal({ isOpen: false, po: null })}
                                disabled={submittingDebitNote}
                                className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitDebitNote}
                                disabled={submittingDebitNote || !debitNoteForm.invoiceNo.trim()}
                                className="px-4 py-2 text-sm font-bold text-white bg-red-600 border-0 rounded-lg hover:bg-red-700 flex items-center gap-2 shadow-sm disabled:opacity-50"
                            >
                                {submittingDebitNote ? <Spinner size="sm" className="text-white" /> : 'Confirm & Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <EditPurchaseOrder
                isOpen={editModalOpen}
                onClose={closeEditModal}
                purchaseOrder={selectedEditPo}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

export default ViewPO;